import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy,Component,Input } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyListModule as MatListModule } from '@angular/material/legacy-list';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Store } from '@ngrx/store';
import { BehaviorSubject,combineLatest,Observable,switchMap } from 'rxjs';
import { debounceTime, filter, map, shareReplay, startWith, takeUntil } from 'rxjs/operators';
import { TimeTrackerKagetecComponent,TimeTrackerKagetecDialogData } from '../../../../../components/dashboard/services/time-tracker-kagetec/time-tracker-kagetec.component';
import { WorkDayEntity } from '../../../../../dave-data-module/entities/work-day.entity';
import { ClockInTypeResolver } from '../../../../../dave-data-module/guards/clock-in-type.resolver';
import { EmployeeResolver } from '../../../../../dave-data-module/guards/employee.resolver';
import { PENDING_CHANGES_DEFAULT_MESSAGE } from '../../../../../dave-data-module/guards/pending-changes.guard';
import { WorkDayResolver } from '../../../../../dave-data-module/guards/work-day.resolver';
import { WorkedTimesResolver } from '../../../../../dave-data-module/guards/worked-times.resolver';
import { getFetched$ } from '../../../../../dave-data-module/helper/helper';
import { ClockInDataService } from '../../../../../dave-data-module/services/clockin-data.service';
import { State } from '../../../../../dave-data-module/State';
import { getClockInTypeDictionary,getClockInTypesFetched } from '../../../../../dave-data-module/State/selectors/clock-in-type.selectors';
import { getEmployeeByUserId,getEmployeeDictionary,getEmployeesFetched } from '../../../../../dave-data-module/State/selectors/employees.selectors';
import { getUser } from '../../../../../dave-data-module/State/selectors/users.selectors';
import { getWorkDays,getWorkDaysFetched } from '../../../../../dave-data-module/State/selectors/work-day.selectors';
import { getWorkedTimes,getWorkedTimesFetched } from '../../../../../dave-data-module/State/selectors/worked-times.selectors';
import { AppButtonModule } from '../../../../../dave-utils-module/app-button-module/app-button.module';
import { isNotNullOrUndefined,sameDay } from '../../../../../helper/helper';
import { LengthPipe } from '../../../../../helper/length.pipe';
import { UserAdministrationMeta } from '../../../../../helper/page-metadata';
import { DayTimeTrackerComponent,DayTimeTrackerComponentDialogData } from '../../../../../time-tracker/components/day-time-tracker/day-time-tracker.component';
import { TimeTrackerMultipleComponent } from '../../../../../time-tracker/components/time-tracker-multiple/time-tracker-multiple.component';
import { Actions, ofType } from '@ngrx/effects';
import { ClockInActionTypes } from '../../../../../dave-data-module/State/actions/clock-in.actions';
type clockInComponent = 'timer-popup' | 'timer-multiple-popup' | 'day-timer-popup';
@Component({
    selector: 'app-construction-diary-time-tracking[day][commissionId]',
    standalone: true,
    imports: [CommonModule, AppButtonModule, FontAwesomeModule, MatListModule, MatTooltipModule, LengthPipe],
    templateUrl: './construction-diary-time-tracking.component.html',
    styleUrls: ['./construction-diary-time-tracking.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConstructionDiaryTimeTrackingComponent {
    private day$ = new BehaviorSubject<Date>(null);
    @Input() set day(v: Date) {
        if (v?.getTime() !== this.day$.value?.getTime()) {
            this.day$.next(v);
        }
    }
    private commissionId$ = new BehaviorSubject<number>(null);
    @Input() set commissionId(v: number) {
        if (v !== this.commissionId$.value) {
            this.commissionId$.next(v);
        }
    }

    preferredClockInComponent$: Observable<clockInComponent>;
    workDays$: Observable<{ IsValid: boolean; Timespan: number; EmployeeName: string; ItsMe: boolean }[]>;

    constructor(
        store: Store<State>,
        actions$: Actions,
        clockInDataService: ClockInDataService,
        private dialog: MatDialog,
        employeeResolver: EmployeeResolver,
        workDayResolver: WorkDayResolver,
        workedTimesResolver: WorkedTimesResolver,
        clockInTypeResolver: ClockInTypeResolver,
    ) {
        const employee$ = store.select(getUser).pipe(switchMap((user) => store.select(getEmployeeByUserId({ userId: user.Id }))));
        const employees$ = getFetched$(store, getEmployeesFetched, getEmployeeDictionary, employeeResolver);
        const workDays$ = getFetched$(store, getWorkDaysFetched, getWorkDays, workDayResolver);
        const clockIns$ = this.commissionId$.pipe(
            filter(isNotNullOrUndefined),
            switchMap((commissionId) => actions$.pipe(
                ofType(
                    ClockInActionTypes.AddClockInsSuccess,
                    ClockInActionTypes.ModifyClockInsSuccess,
                    ClockInActionTypes.DeleteClockInsSuccess
                ),
                debounceTime(500),
                startWith(null),
                switchMap(() => clockInDataService.get$({ commissionId })),
            )),
        );
        const workedTimes$ = getFetched$(store, getWorkedTimesFetched, getWorkedTimes, workedTimesResolver);
        const clockInTypes$ = getFetched$(store, getClockInTypesFetched, getClockInTypeDictionary, clockInTypeResolver);

        this.workDays$ = combineLatest([employee$, employees$, workDays$, clockIns$, workedTimes$, clockInTypes$, this.day$.pipe(filter(isNotNullOrUndefined)), this.commissionId$.pipe(filter(isNotNullOrUndefined))]).pipe(
            map(([employee, employees, workDays, filteredClockIns, workedTimes, clockInTypes, day, commissionId]) => {
                // const filteredClockIns: ClockInEntity[] = clockIns.filter((c) => c.CommissionId === commissionId);
                const filteredWorkDays: WorkDayEntity[] = workDays.filter((wd) => sameDay(wd.Timestamp, day) && filteredClockIns.some((ci) => ci.WorkDayId === wd.Id && ci.CommissionId === commissionId));
                const filteredWorkTimes = workedTimes.filter((workTime) => clockInTypes[workTime.ClockInTypeId]?.ShouldBeExported && workTime.CommissionId === commissionId);

                // todo ggf nach employee gruppieren ?
                return filteredWorkDays.map((wd) => {
                    let Timespan = 0;
                    Timespan = filteredWorkTimes.filter((workTime) => wd.Id === workTime.WorkDayId).reduce((previousValue, currentValue) => previousValue + (currentValue.Timespan || 0), 0);
                    return {
                        IsValid: wd.IsValid,
                        Timespan,
                        EmployeeName: employees[wd.EmployeeId]?.DisplayName,
                        ItsMe: wd.EmployeeId === employee.Id,
                    };
                });
            }),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
        this.preferredClockInComponent$ = clockInDataService.getUserSettingsForClockIns$().pipe(
            map((settings) => {
                if (settings.ShowTimeTrackingMultiplePopup) {
                    return 'timer-multiple-popup';
                }
                if (settings.ShowTimeTrackingPopup) {
                    return 'timer-popup';
                }
                return 'day-timer-popup';
            }),
        );
    }

    protected readonly UserAdministrationMeta = UserAdministrationMeta;

    onTimeTrackerClick(clockInComponent: clockInComponent) {
        switch (clockInComponent) {
            case 'timer-popup':
                this.dialog.open<TimeTrackerKagetecComponent, TimeTrackerKagetecDialogData>(TimeTrackerKagetecComponent, {
                    ...TimeTrackerKagetecComponent.DefaultConfig,
                    data: {
                        DefaultValues: {
                            CommissionId: this.commissionId$.value,
                            Date: this.day$.value,
                        },
                    },
                });
                break;
            case 'timer-multiple-popup':
                this.dialog.open<TimeTrackerMultipleComponent>(TimeTrackerMultipleComponent, TimeTrackerMultipleComponent.DefaultConfig);
                break;
            case 'day-timer-popup':
            default:
                const dialogRef = this.dialog.open<DayTimeTrackerComponent, DayTimeTrackerComponentDialogData>(DayTimeTrackerComponent, {
                    ...DayTimeTrackerComponent.DefaultConfig,
                    data: {
                        date: this.day$.value,
                        defaultCommissionId: this.commissionId$.value,
                    },
                });
                dialogRef
                    .backdropClick()
                    .pipe(takeUntil(dialogRef.beforeClosed()))
                    .subscribe((event) => {
                        if (dialogRef.componentInstance.canDeactivate() || confirm(PENDING_CHANGES_DEFAULT_MESSAGE)) {
                            dialogRef.close();
                        }
                    });
                break;
        }
    }
}
